import React, { useState, useCallback, useEffect } from 'react';
import {
  Modal,
  Box,
  Text,
  Button,
  Group,
  Stack,
  Alert,
  Progress,
  LoadingOverlay,
  TextInput,
  Select,
  Divider,
  Table,
  ScrollArea,
  ActionIcon,
} from '@mantine/core';
import { Dropzone, FileWithPath } from '@mantine/dropzone';
import {
  IconUpload,
  IconX,
  IconFileText,
  IconAlertCircle,
  IconCheck,
  IconShield,
  IconTableImport,
  IconCornerRightDown,
} from '@tabler/icons';
import { useAuth0 } from '@auth0/auth0-react';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useBagelObjects } from '../hooks/useBagelObjects';
import { useUploadIntegrationRecords } from '../hooks/useIntegrations';
import {
  parseCSVFile as parseCSVFileUtil,
  processCSVData as processCSVDataUtil,
  type ColumnMapping,
  type CSVPreviewData,
} from '../utils/csvParsing';

interface CSVUploadModalProps {
  opened: boolean;
  onClose: () => void;
  integrationKey: string;
  onUploadSuccess?: (data: any) => void;
  prefilledData?: {
    feedbackName: string;
    feedbackCategory: string;
    originSystem: string;
  };
  disableMetadataFields?: boolean;
}

interface ParsedCSVData {
  feedbackName: string;
  feedbackCategory: string;
  originSystem: string;
  records: Array<{
    originId: string;
    linkedAccountId?: string;
    createdAt: string;
    submitterEmail?: string;
    title: string;
    description: string;
    priority?: string;
  }>;
}

interface FormValues {
  feedbackName: string;
  feedbackCategory: string;
  originSystem: string;
}

export const CSVUploadModal: React.FC<CSVUploadModalProps> = ({
  opened,
  onClose,
  integrationKey,
  onUploadSuccess,
  prefilledData,
  disableMetadataFields = false,
}) => {
  const auth0 = useAuth0();
  const [files, setFiles] = useState<FileWithPath[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [parseError, setParseError] = useState<string | null>(null);
  const [csvPreview, setCsvPreview] = useState<CSVPreviewData | null>(null);
  const [columnMapping, setColumnMapping] = useState<ColumnMapping>({});
  const [parsedData, setParsedData] = useState<ParsedCSVData | null>(null);
  const [isParsingCsv, setIsParsingCsv] = useState(false);
  const [fullCsvData, setFullCsvData] = useState<{ headers: string[]; rows: string[][] } | null>(
    null,
  );

  // Get Bagel objects for feedback category options
  const { data: bagelObjects, isLoading: isBagelObjectsLoading } = useBagelObjects(auth0);

  // Hook for uploading integration records
  const { mutateAsync: uploadRecords, isPending: isUploadingRecords } =
    useUploadIntegrationRecords();

  // Form for object metadata
  const form = useForm<FormValues>({
    initialValues: {
      feedbackName: prefilledData?.feedbackName || '',
      feedbackCategory: prefilledData?.feedbackCategory || '',
      originSystem: prefilledData?.originSystem || '',
    },
    validate: {
      feedbackName: (value) => (value.trim().length < 1 ? 'Feedback name is required' : null),
      feedbackCategory: (value) => (value.length < 1 ? 'Feedback category is required' : null),
      originSystem: (value) => (value.trim().length < 1 ? 'Origin system is required' : null),
    },
  });

  // Update form values when prefilledData changes
  useEffect(() => {
    if (prefilledData) {
      form.setValues({
        feedbackName: prefilledData.feedbackName,
        feedbackCategory: prefilledData.feedbackCategory,
        originSystem: prefilledData.originSystem,
      });
    }
  }, [prefilledData, form]);

  // Convert Bagel objects to select options
  const bagelObjectOptions =
    bagelObjects?.map((obj: any) => ({
      value: obj.key,
      label: obj.label || obj.key,
    })) || [];

  // Available field options for column mapping (state to allow dynamic updates)
  const [fieldOptions, setFieldOptions] = useState([
    { value: '', label: 'Skip this column' },
    { value: 'originId', label: 'Origin ID', required: true },
    { value: 'linkedAccountId', label: 'Linked Account ID', required: true },
    { value: 'createdAt', label: 'Created At', required: true },
    { value: 'title', label: 'Title' },
    { value: 'description', label: 'Description' },
    { value: 'submitterEmail', label: 'Submitter Email' },
    { value: 'priority', label: 'Priority' },
  ]);

  // Custom item component for Select options
  const SelectItem = React.forwardRef<HTMLDivElement, any>(
    ({ label, required, ...others }, ref) => (
      <div ref={ref} {...others}>
        <Group spacing="xs" noWrap>
          <Text size="sm">{label}</Text>
          {required && (
            <Text color="red" size="sm">
              *
            </Text>
          )}
        </Group>
      </div>
    ),
  );

  const handleDrop = useCallback(
    (acceptedFiles: FileWithPath[]) => {
      setFiles(acceptedFiles);
      setParseError(null);
      setParsedData(null);
      setCsvPreview(null);
      setColumnMapping({});
      setIsParsingCsv(true);

      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        // Pre-fill feedback name with filename if it's empty
        if (!form.values.feedbackName.trim()) {
          const filename = file.name.replace('.csv', '').replace(/[_-]/g, ' ');
          form.setFieldValue('feedbackName', filename);
        }
        parseCSVFile(file);
      }
    },
    [form],
  );

  const parseCSVFile = async (file: FileWithPath) => {
    try {
      const { headers, allRows, previewRows } = await parseCSVFileUtil(file);
      processCSVData(headers, allRows, previewRows);
    } catch (error: any) {
      setParseError(error.message || 'Failed to parse CSV file. Please check the format.');
      console.error('CSV read error:', error);
      setIsParsingCsv(false);
    }
  };

  const processCSVData = (headers: string[], allRows: string[][], previewRows: string[][]) => {
    try {
      const result = processCSVDataUtil(headers, allRows, previewRows);

      // Add column names as field options if they don't already exist
      const newFieldOptions = [...fieldOptions];
      headers.forEach((header) => {
        if (!newFieldOptions.find((option) => option.value === header)) {
          newFieldOptions.push({ value: header, label: header });
        }
      });
      setFieldOptions(newFieldOptions);

      setCsvPreview(result.csvPreview);
      setFullCsvData(result.fullCsvData);
      setColumnMapping(result.columnMapping);
    } catch (error) {
      setParseError('Failed to process CSV data. Please check the format.');
      console.error('CSV processing error:', error);
    } finally {
      setIsParsingCsv(false);
    }
  };

  const handleColumnMappingChange = (csvColumn: string, fieldName: string) => {
    setColumnMapping((prev) => ({
      ...prev,
      [csvColumn]: fieldName,
    }));
    // Clear parse error when user makes changes
    setParseError(null);
  };

  const generateFinalData = () => {
    console.log('generateFinalData - csvPreview:', !!csvPreview);
    console.log('generateFinalData - form.isValid():', form.isValid());
    console.log('generateFinalData - form.values:', form.values);

    if (!csvPreview || !form.isValid()) {
      console.log('generateFinalData - early return: csvPreview or form invalid');
      return null;
    }

    // Check if required fields are mapped using the fieldOptions
    const requiredFields = fieldOptions
      .filter((option) => option.required)
      .map((option) => option.value);

    const mappedFields = Object.values(columnMapping);
    const missingRequired = requiredFields.filter((field) => !mappedFields.includes(field));

    console.log('generateFinalData - requiredFields:', requiredFields);
    console.log('generateFinalData - mappedFields:', mappedFields);
    console.log('generateFinalData - missingRequired:', missingRequired);
    console.log('generateFinalData - columnMapping:', columnMapping);

    if (missingRequired.length > 0) {
      const missingLabels = missingRequired.map(
        (field) => fieldOptions.find((option) => option.value === field)?.label || field,
      );
      console.log('generateFinalData - missing required fields:', missingLabels);
      setParseError(`Please map the following required fields: ${missingLabels.join(', ')}`);
      return null;
    }

    // Parse ALL CSV rows (not just preview) with the column mapping and create records
    const records = fullCsvData!.rows.map((row, index) => {
      const record: any = {};

      // Map each CSV column to the target field based on columnMapping
      Object.entries(columnMapping).forEach(([csvColumn, targetField]) => {
        if (targetField && targetField !== '') {
          const columnIndex = fullCsvData!.headers.indexOf(csvColumn);
          if (columnIndex !== -1) {
            record[targetField] = row[columnIndex] || '';
          }
        }
      });

      return record;
    });

    const finalData = {
      feedbackName: form.values.feedbackName,
      feedbackCategory: form.values.feedbackCategory,
      originSystem: form.values.originSystem,
      columnMapping: columnMapping,
      records: records,
    };

    console.log('generateFinalData - finalData with records:', finalData);
    console.log('generateFinalData - records count:', records.length);
    console.log('generateFinalData - first 3 records:', records.slice(0, 3));
    return finalData;
  };

  const handleSubmit = async () => {
    const submissionData = generateFinalData();
    console.log('submissionData', submissionData);
    if (!submissionData) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress((prev) => {
          if (prev >= 80) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 20;
        });
      }, 500);

      // Use the actual API hook to upload records
      const result = await uploadRecords({
        key: integrationKey,
        recordsData: {
          feedbackName: submissionData.feedbackName,
          feedbackCategory: submissionData.feedbackCategory,
          originSystem: submissionData.originSystem,
          records: submissionData.records,
        },
      });

      clearInterval(progressInterval);
      setUploadProgress(100);

      console.log('Upload successful:', result);

      // Show success notification
      notifications.show({
        title: 'Upload Successful',
        message: `Successfully uploaded ${result.summary?.processedCount || submissionData.records.length} records to ${integrationKey} integration.`,
        color: 'green',
        icon: <IconCheck size={16} />,
        autoClose: 5000,
      });

      setTimeout(() => {
        onUploadSuccess?.(result);
        handleClose();
      }, 1000);
    } catch (error: any) {
      console.log('Upload error:', error, error.response);

      // Show error notification
      notifications.show({
        title: 'Upload Failed',
        message:
          error?.response?.data?.error ||
          error?.message ||
          'Failed to upload CSV data. Please try again.',
        color: 'red',
        icon: <IconAlertCircle size={16} />,
        autoClose: 8000,
      });

      // Also set parse error for inline display
      setParseError(
        error?.response?.data?.message ||
          error?.message ||
          'Failed to upload CSV data. Please try again.',
      );
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFiles([]);
    setParseError(null);
    setParsedData(null);
    setCsvPreview(null);
    setFullCsvData(null);
    setColumnMapping({});
    setUploading(false);
    setUploadProgress(0);
    setIsParsingCsv(false);
    form.reset();
    onClose();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      size="xl"
      withCloseButton={false}
      padding={0}
      closeOnClickOutside
      closeOnEscape
      centered
      styles={{
        content: {
          display: 'flex',
          flexDirection: 'column',
          height: '80vh',
        },
        body: {
          padding: 0,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
        },
      }}
    >
      {/* Sticky Header */}
      <Box style={{ position: 'sticky', top: 0, zIndex: 10, backgroundColor: 'white' }}>
        <Group p="xs" position="apart">
          <Group spacing={8}>
            <IconTableImport size={18} stroke={1.5} />
            <Text fw={500}>Upload CSV Data</Text>
          </Group>
          <ActionIcon onClick={handleClose}>
            <IconX size={16} />
          </ActionIcon>
        </Group>
        <Divider />
      </Box>

      <LoadingOverlay visible={uploading || isUploadingRecords} overlayBlur={2} />

      {/* Scrollable Content */}
      <Box style={{ flex: 1, overflow: 'auto' }}>
        <Text size="sm" color="dimmed" p="sm" style={{ right: 0, left: 0, position: 'sticky' }}>
          Upload a CSV file with your feedback data. Required columns: <b>originId</b>,{' '}
          <b>linkedAccountId</b>, and <b>createdAt</b>. Optional columns include <b>title</b>,{' '}
          <b>description</b>, <b>submitterEmail</b>, <b>priority</b>, and others. You can map
          columns to any custom fields (use camelCase for best results), or skip columns you don't
          want to import.
        </Text>

        {/* Form fields for object metadata */}
        <Box
          p="sm"
          sx={{
            zIndex: 1,
            backgroundColor: '#5c5ceb0d',
            right: '0',
            left: '0',
            position: 'sticky',
          }}
        >
          <Group grow spacing="md">
            <TextInput
              label="Feedback Name"
              placeholder="Enter feedback name (e.g., gong_feature_request)"
              required
              disabled={disableMetadataFields}
              {...form.getInputProps('feedbackName')}
            />

            <Select
              label="Feedback Category"
              placeholder="Select feedback category"
              required
              data={bagelObjectOptions}
              searchable
              disabled={isBagelObjectsLoading || disableMetadataFields}
              {...form.getInputProps('feedbackCategory')}
            />

            <TextInput
              label="Origin System"
              placeholder="Enter origin system (e.g., gong, zendesk)"
              required
              disabled={disableMetadataFields}
              {...form.getInputProps('originSystem')}
            />
          </Group>
        </Box>

        {!csvPreview && !isParsingCsv && (
          <Dropzone
            m="sm"
            onDrop={handleDrop}
            accept={{
              'text/csv': ['.csv'],
              'application/vnd.ms-excel': ['.csv'],
            }}
            maxFiles={1}
            maxSize={5 * 1024 * 1024} // 5MB
          >
            <Group position="center" spacing="xl" style={{ minHeight: 320, pointerEvents: 'none' }}>
              <Dropzone.Accept>
                <IconTableImport size={50} stroke={1.5} />
              </Dropzone.Accept>
              <Dropzone.Reject>
                <IconX size={50} stroke={1.5} />
              </Dropzone.Reject>
              <Dropzone.Idle>
                <IconFileText size={50} stroke={1.5} />
              </Dropzone.Idle>

              <div>
                <Text size="xl" inline>
                  Drag CSV file here or click to select
                </Text>
                <Text size="sm" color="dimmed" inline mt={7}>
                  File should not exceed 5MB
                </Text>
              </div>
            </Group>
          </Dropzone>
        )}

        {isParsingCsv && (
          <Box style={{ textAlign: 'center', padding: '40px 20px' }}>
            <LoadingOverlay visible={true} overlayBlur={0} />
            <Text size="lg" weight={500} mb="sm">
              Processing CSV file...
            </Text>
            <Text size="sm" color="dimmed">
              Please wait while we parse your data
            </Text>
          </Box>
        )}

        {parseError && (
          <Alert
            sx={{ right: '0', left: '0', position: 'sticky' }}
            icon={<IconAlertCircle size={16} />}
            title="Parse Error"
            color="red"
          >
            {parseError}
          </Alert>
        )}

        {csvPreview && (
          <Box>
            <Table striped highlightOnHover sx={{ overflow: 'auto' }}>
              <thead
                style={{
                  position: 'sticky',
                  top: 0,
                  // zIndex: 5,
                  backgroundColor: 'white',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                }}
              >
                <tr>
                  {csvPreview.headers.map((header, index) => (
                    <th
                      key={index}
                      style={{
                        minWidth: 150,
                        maxWidth: 200,
                        backgroundColor: 'white',
                        borderBottom: '2px solid #e9ecef',
                      }}
                    >
                      <Stack spacing={4}>
                        <Group spacing={4} noWrap>
                          <Text
                            size="xs"
                            weight={500}
                            title={header}
                            style={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              display: 'block',
                            }}
                          >
                            {header}
                          </Text>
                          <IconCornerRightDown size={16} stroke={1.5} />
                        </Group>
                        <Select
                          size="xs"
                          placeholder="Select field"
                          data={fieldOptions}
                          value={columnMapping[header] || ''}
                          onChange={(value) => handleColumnMappingChange(header, value || '')}
                          searchable
                          creatable
                          getCreateLabel={(query) => `+ Create "${query}"`}
                          onCreate={(query) => {
                            const newOption = { value: query, label: query };
                            setFieldOptions((prev) => [...prev, newOption]);
                            handleColumnMappingChange(header, query);
                            return newOption;
                          }}
                          itemComponent={SelectItem}
                        />
                      </Stack>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {csvPreview.rows.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {row.map((cell, cellIndex) => (
                      <td key={cellIndex}>
                        <Text
                          size="xs"
                          title={cell}
                          style={{
                            maxWidth: 120,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            display: 'block',
                          }}
                        >
                          {cell}
                        </Text>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </Box>
        )}

        {uploading && (
          <Box>
            <Text size="sm" mb="xs">
              Uploading data...
            </Text>
            <Progress value={uploadProgress} />
          </Box>
        )}
      </Box>

      {/* Sticky Footer */}
      <Box
        style={{
          position: 'sticky',
          bottom: 0,
          zIndex: 10,
          backgroundColor: 'white',
          boxShadow: csvPreview ? '0 -4px 8px rgba(0,0,0,0.1)' : 'none',
        }}
      >
        <Divider />
        <Group position="apart" p="xs">
          <Group>
            {csvPreview && (
              <Button
                variant="subtle"
                size="sm"
                onClick={() => {
                  setFiles([]);
                  setCsvPreview(null);
                  setFullCsvData(null);
                  setColumnMapping({});
                  setIsParsingCsv(false);
                  setParseError(null);
                }}
              >
                Choose different file
              </Button>
            )}
          </Group>
          <Group>
            <Button variant="outline" onClick={handleClose} disabled={uploading}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={!csvPreview || uploading || !form.isValid()}
              loading={uploading}
            >
              Upload & Sync {csvPreview ? `(${csvPreview.totalRows} records)` : ''}
            </Button>
          </Group>
        </Group>
      </Box>
    </Modal>
  );
};
